import { Command } from "commander";
import chalk from "chalk";
import { confirm } from "@inquirer/prompts";
import { createSyncManager } from "@fathom/sync";
import type { SyncConfig, SyncItem, SyncStatus } from "@fathom/sync";
import { readFileSync, existsSync } from "node:fs";
import { resolve } from "node:path";
import { withSpinner } from "../ux/spinner.js";
import { fatalError } from "../ux/errors.js";

interface SyncCommandOptions {
  provider: "linear" | "github" | "notion" | "jira";
  projectKey: string;
  pull?: boolean;
  dryRun?: boolean;
  yes?: boolean;
}

function loadSyncConfig(options: SyncCommandOptions): SyncConfig {
  // Try loading from .fathom/sync-config.json first, then override with CLI flags
  const configPath = resolve(process.cwd(), ".fathom", "sync-config.json");
  let config: Partial<SyncConfig> = {};

  if (existsSync(configPath)) {
    try {
      config = JSON.parse(readFileSync(configPath, "utf-8"));
    } catch {
      // Ignore corrupt config
    }
  }

  return {
    provider: options.provider ?? config.provider ?? "linear",
    projectKey: options.projectKey ?? config.projectKey ?? "",
    apiKey: config.apiKey,
    apiKeyEnv: config.apiKeyEnv,
    statusMapping: config.statusMapping,
  };
}

function loadPendingSlices(): SyncItem[] {
  // Load slices from the feature registry
  const registryPath = resolve(process.cwd(), ".fathom", "registry.json");
  if (!existsSync(registryPath)) return [];

  try {
    const data = JSON.parse(readFileSync(registryPath, "utf-8"));
    if (!Array.isArray(data.features)) return [];

    return data.features.map((f: {
      id: string;
      name: string;
      keywords?: string[];
      estimated_tokens?: number;
      status?: string;
    }) => ({
      sliceId: f.id,
      title: f.name,
      description: f.name,
      category: "feature",
      complexity: "M",
      priority: "P2",
      estimatedTokens: f.estimated_tokens ?? 10000,
      estimatedCost: (f.estimated_tokens ?? 10000) * 0.00004,
      acceptanceCriteria: "",
      labels: f.keywords ?? [],
    }));
  } catch {
    return [];
  }
}

function formatStatus(status: SyncStatus): string {
  const colors: Record<SyncStatus, (s: string) => string> = {
    backlog: chalk.dim,
    todo: chalk.white,
    in_progress: chalk.blue,
    review: chalk.yellow,
    done: chalk.green,
    cancelled: chalk.red,
  };
  return (colors[status] ?? chalk.white)(status);
}

export const syncCommand = new Command("sync")
  .description("Sync spec slices to external PM tools (Linear, GitHub, Notion, Jira)")
  .requiredOption("--provider <provider>", "PM tool provider (linear, github, notion, jira)")
  .requiredOption("--project-key <key>", "Project key (Linear team key, GitHub owner/repo, Notion database ID, Jira host/project)")
  .option("--pull", "Only pull status updates, don't push new slices")
  .option("--dry-run", "Show what would be synced without making changes")
  .option("-y, --yes", "Skip confirmation prompt")
  .action(async (options: SyncCommandOptions) => {
    try {
      const config = loadSyncConfig(options);

      if (!config.projectKey) {
        fatalError(
          "Missing project key",
          "A project key is required to sync with external tools",
          "Use --project-key flag or set it in .fathom/sync-config.json",
        );
      }

      console.log(chalk.bold(`\nFathom \u2014 Sync to ${config.provider.charAt(0).toUpperCase() + config.provider.slice(1)}`));
      console.log(chalk.dim("  " + "\u2500".repeat(40)));

      const manager = createSyncManager(config);

      // ── Push new slices ──
      if (!options.pull) {
        const slices = loadPendingSlices();

        if (slices.length === 0) {
          console.log(chalk.dim("\n  No pending slices found in registry."));
          console.log(chalk.dim("  Run 'fathom intake' first to create spec slices."));
        } else if (options.dryRun) {
          console.log(chalk.dim(`\n  [dry-run] Would sync ${slices.length} slice(s):`));
          for (const s of slices) {
            console.log(chalk.dim(`    - ${s.sliceId}: ${s.title}`));
          }
        } else {
          // Confirmation prompt (skip with --yes)
          if (!options.yes) {
            const proceed = await confirm({
              message: `Sync ${slices.length} slices to ${config.provider}?`,
              default: true,
            });
            if (!proceed) {
              console.log(chalk.dim("\n  Aborted."));
              return;
            }
          }

          console.log(chalk.dim(`\n  Syncing ${slices.length} pending slice(s) to ${config.provider} project ${config.projectKey}...\n`));

          const results = await withSpinner(`Syncing slices to ${config.provider}...`, async () =>
            manager.syncSlices(slices)
          );

          if (results.length === 0) {
            console.log(chalk.dim("  All slices already synced."));
          } else {
            for (const r of results) {
              console.log(chalk.green(`  \u2713 ${r.externalId} \u2192 ${r.url}`));
            }
          }
        }
      }

      // ── Pull status updates ──
      if (!options.dryRun) {
        console.log(chalk.dim("\n  Pulling status updates..."));

        const updates = await withSpinner("Pulling status updates...", async () =>
          manager.pullStatus()
        );

        if (updates.length === 0) {
          console.log(chalk.dim("  No status changes."));
        } else {
          for (const u of updates) {
            console.log(
              `  \u2713 ${u.sliceId}: ${formatStatus(u.newStatus)} (was: ${formatStatus(u.previousStatus)})`,
            );
          }
        }
      }

      // ── Summary ──
      const ledger = await manager.getLedger();
      if (ledger.length > 0) {
        console.log(chalk.dim(`\n  Ledger: ${ledger.length} synced item(s) in .fathom/sync-ledger.json`));
      }

      console.log();
    } catch (err: unknown) {
      if (err && typeof err === "object" && "name" in err && (err as Error).name === "ExitPromptError") {
        console.log(chalk.dim("\n  Exiting.\n"));
        return;
      }
      fatalError(
        "Sync failed",
        (err as Error).message,
        "Check your API key and project key configuration",
      );
    }
  });
